// Vetting status + compliance pack for one contractor — RON-5466 Phase 4.
//
// Shown on the lookup step and again on review, because the host needs the same facts at both
// points: once to decide whether to carry on, once to confirm what they are committing to.
//
// TWO SEPARATE THINGS, deliberately not merged into one verdict:
//
//   vetting    is this person approved, and up to what date
//   documents  is their insurance / safety paperwork still in date
//
// They lapse independently. A contractor can be cleared until next June with insurance that ran
// out last week, so collapsing both into a single "compliant / not compliant" line would hide
// exactly the case reception needs to catch.

import { useTranslation } from "react-i18next";

import { formatISOToLong } from "../visitorDateUtils.js";
import {
  DOCUMENT_STATE,
  VETTING_STATE,
  getDocumentState,
  getVettingLabelKey,
  getVettingTone,
  isContractorBlocked,
} from "../visitorConstants.js";

const DOCUMENT_STATE_LABEL_KEYS = {
  [DOCUMENT_STATE.VALID]: "react_visitors_doc_valid",
  [DOCUMENT_STATE.EXPIRING]: "react_visitors_doc_expiring",
  [DOCUMENT_STATE.EXPIRED]: "react_visitors_doc_expired",
  [DOCUMENT_STATE.NO_EXPIRY]: "react_visitors_doc_no_expiry",
};

const ContractorVettingCard = ({ contractor, todayISO, endDateISO }) => {
  const { t } = useTranslation("common");

  if (!contractor) return null;

  const documents = contractor.documents || [];
  const isBlocked = isContractorBlocked(contractor);

  // The clearance date is only meaningful once we know when they are coming, so it is called out
  // separately from the plain "vetted until" row when a booking would run past it.
  const expiresBeforeVisit =
    contractor.vettedUntil &&
    endDateISO &&
    String(endDateISO) > String(contractor.vettedUntil);

  return (
    <div className="visitor-card">
      <p className="visitor-card__title">{t("react_visitors_vetting_title")}</p>

      <div className="visitor-vetting__head">
        {/* Five states, three colours: the class is keyed on the TONE, not the state, so the CSS
            keeps one rule per colour rather than one per state. */}
        <span
          className={`visitor-badge visitor-badge--vetting-${getVettingTone(
            contractor.vettingState
          )}`}
        >
          {t(getVettingLabelKey(contractor.vettingState))}
        </span>
        {contractor.vettedUntil && (
          <span className="visitor-vetting__until">
            {t("react_visitors_vetted_until_s").replace(
              "%s",
              formatISOToLong(contractor.vettedUntil, t)
            )}
          </span>
        )}
      </div>

      {/* WHY they are blocked, not just that they are — the host has to know who to talk to. The
          specific reason is far more useful than the generic line, so it wins when the register has
          one; the generic message is the fallback rather than an addition, because showing both
          means two red boxes that each end "contact facilities". */}
      {isBlocked && (
        <p className="visitor-vetting__reason">
          {contractor.blockedReason || t("react_visitors_vetting_blocked_message")}
        </p>
      )}

      {expiresBeforeVisit && (
        <p className="visitor-vetting__reason">
          {t("react_visitors_vetting_expired_message")}
        </p>
      )}

      {/* C11b — the note on a VETTED contractor. Added 21-October-2026.
          Prototype: `ok && e('p', …, 'Vetted, so no documents are needed for this visit …')`.

          WHY IT WAS HELD AND WHY IT SHIPS NOW. The 10-October revision worded this as
          "Pre-approved, so their company documents won't be asked for again. They'll get a short
          link for this visit's documents only … It closes on the visit date." That was not copy:
          it was the visible edge of a two-tier model (company documents vetted once, visit
          documents on a short-lived link) AND it promised a closing date the Laravel team had
          asked us to remove six days earlier, because the portal has none.

          The 15-October revision removed the two tier model — the prototype's own comment now
          reads "no visit mode: vetting is decoupled from visits" — and the note became the plain
          sentence below. It promises nothing we cannot keep, so there is nothing left to hold.

          Only when VETTED. An in-flight or expired contractor must not be told no documents are
          needed; for them it is simply untrue. */}
      {contractor.vettingState === VETTING_STATE.VETTED && !expiresBeforeVisit && (
        <p className="visitor-vetting__note">{t("react_visitors_vetting_vetted_note")}</p>
      )}

      {documents.length > 0 ? (
        <ul className="visitor-docs">
          {documents.map((document) => {
            const state = getDocumentState(document, todayISO);

            return (
              <li className="visitor-docs__item" key={document.name}>
                <span className="visitor-docs__name">{document.name}</span>
                <span className="visitor-docs__expiry">
                  {document.expiresOn ? formatISOToLong(document.expiresOn, t) : "—"}
                </span>
                <span className={`visitor-badge visitor-badge--doc-${state}`}>
                  {t(DOCUMENT_STATE_LABEL_KEYS[state])}
                </span>
              </li>
            );
          })}
        </ul>
      ) : (
        // An empty pack is a fact worth stating. Rendering nothing would read as "not loaded".
        <p className="visitor-card__hint">{t("react_visitors_docs_none")}</p>
      )}
    </div>
  );
};

export default ContractorVettingCard;
